import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';
import {
  LayoutDashboard, Route, Truck, FileText, Clock, Trophy,
  Users, Car, MapPin, Receipt, CreditCard,
  BarChart3,
  Settings, LogOut, Menu, X, ChevronDown, ChevronRight,
} from 'lucide-react';

// ========== Menu Config ==========

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface MenuSection {
  role: string;
  title: string;
  emoji: string;
  items: MenuItem[];
}

const SECTIONS: MenuSection[] = [
  {
    role: 'driver',
    title: 'Conductor',
    emoji: '🚗',
    items: [
      { label: 'Dashboard', path: '/app/driver/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Mis Rutas', path: '/app/driver/dashboard', icon: <Route size={18} /> },
      { label: 'Vehículos', path: '/app/driver/vehicles', icon: <Truck size={18} /> },
      { label: 'Documentos', path: '/app/driver/documents', icon: <FileText size={18} /> },
      { label: 'Disponibilidad', path: '/app/driver/availability', icon: <Clock size={18} /> },
      { label: 'Earnings', path: '/app/driver/gamification', icon: <Trophy size={18} /> },
    ],
  },
  {
    role: 'fleet_manager',
    title: 'Mi Flota',
    emoji: '🏢',
    items: [
      { label: 'Dashboard', path: '/app/fleet/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Conductores', path: '/app/fleet/drivers', icon: <Users size={18} /> },
      { label: 'Vehículos', path: '/app/fleet/vehicles', icon: <Car size={18} /> },
      { label: 'Trips', path: '/app/fleet/trips', icon: <MapPin size={18} /> },
      { label: 'Facturación', path: '/app/fleet/invoicing', icon: <Receipt size={18} /> },
      { label: 'Documentos', path: '/app/fleet/documents', icon: <FileText size={18} /> },
      { label: 'Pagos', path: '/app/fleet/invoices', icon: <CreditCard size={18} /> },
    ],
  },
  {
    role: 'customer',
    title: 'Cliente',
    emoji: '📦',
    items: [
      { label: 'Dashboard', path: '/app/customer/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Rutas', path: '/app/customer/routes', icon: <Route size={18} /> },
      { label: 'Billing', path: '/app/customer/billing', icon: <CreditCard size={18} /> },
      { label: 'Reportes', path: '/app/customer/billing', icon: <BarChart3 size={18} /> },
    ],
  },
];

// ========== Component ==========

export function UnifiedSidebar() {
  const { user, logout, hasRole } = useUnifiedAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const visibleSections = user?.godMode ? SECTIONS : SECTIONS.filter(s => hasRole(s.role));

  const toggleSection = (role: string) => {
    setCollapsed(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const navLinkClass = (path: string) => {
    const active = location.pathname === path;
    return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
      active
        ? 'bg-[#FDD238]/15 text-[#FDD238] font-medium'
        : 'text-gray-400 hover:text-white hover:bg-white/5'
    }`;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-5 border-b border-white/10">
        <span className="text-[#FDD238] font-bold text-xl tracking-tight">Nexus</span>
        <span className="text-gray-500 text-xs ml-2">Partrunner</span>
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {visibleSections.map(section => {
          const isCollapsed = collapsed[section.role];
          return (
            <div key={section.role}>
              <button
                onClick={() => toggleSection(section.role)}
                className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition"
              >
                <span>{section.emoji} {section.title}</span>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              {!isCollapsed && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map(item => (
                    <NavLink
                      key={item.path + item.label}
                      to={item.path}
                      className={navLinkClass(item.path)}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 px-3 py-3 space-y-1">
        <NavLink to="/app/profile" className={navLinkClass('/app/profile')} onClick={() => setMobileOpen(false)}>
          <Settings size={18} />
          Perfil
        </NavLink>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors w-full"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </div>

      {/* User info */}
      {user && (
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-white text-sm font-medium truncate">{user.displayName || user.email || user.phone}</p>
          <p className="text-gray-500 text-xs truncate">{user.email || user.phone}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#111] rounded-lg text-white"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#111]">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-[#111] border-r border-white/10 min-h-screen">
        {sidebarContent}
      </aside>
    </>
  );
}
